/**
 * Canvas semantic color tokens (spec section 14.2 item 5: token purity).
 *
 * The canvas builder and view-grid components previously carried hardcoded
 * rgba() literals for their affordance colors (drop targets, drop glow,
 * reference-link cells, new-row highlight). Spec 14.2 item 5 requires these to
 * be semantic tokens that resolve through the Joy palette, so they track the
 * brand/org/canvas theme instead of being fixed sRGB values. This module is the
 * single allowed home for those color strings; the jest guard
 * `noHardcodedColor.test.ts` asserts no rgba()/hex literal survives in the
 * canvasBuilder and viewGrid component directories outside this file.
 *
 * Mechanism: each token is `rgba(var(--joy-palette-<channel>-mainChannel) / A)`.
 * Joy publishes a space-separated `*-mainChannel` triple (e.g. "25 118 210")
 * for every palette channel at the document root, so `rgba(var(...) / A)` yields
 * the channel's main color at alpha A and re-themes with the palette. A numeric
 * fallback triple is supplied inside each `var()` so that, if the channel
 * variable is ever absent, the token still renders its original color rather
 * than collapsing to transparent (a silent, invisible-affordance regression).
 * The fallback triples are each token's exact pre-tokenization sRGB value.
 *
 * Semantic mapping (original literal -> channel):
 *   drop target valid      rgba(33,150,243,*)  -> primary  (actionable/link blue)
 *   drop target invalid    rgba(211,47,47,*)   -> danger   (rejection red)
 *   drop glow              rgba(76,175,80,*)   -> success  (accept green)
 *   reference-link cell    rgba(25,118,210,*)  -> primary  (link blue)
 *   new-row highlight      rgba(76,175,80,*)   -> success  (additive green)
 *
 * WORDING-PRECISION NOTE (spec section 14.1; slice note to plan): the two
 * view-grid pairs are NOT "row hover". The blue pair is the reference/link edit
 * cell (`.reference-edit-cell`) base and its hover; the green pair is the
 * new-row highlight (`[data-id='__new_row__']`) base and its hover. The green is
 * an additive "new row" affordance (success), not a hover/selection color. Token
 * names below reflect the true affordance, not the visual color.
 */

/** Alias for the Joy per-channel main-color triple, with an sRGB fallback. */
function channel(
  channelName: string,
  fallbackTriple: string,
  alpha: number,
): string {
  return `rgba(var(--joy-palette-${channelName}-mainChannel, ${fallbackTriple}) / ${alpha})`;
}

export const CANVAS_COLOR = {
  /** Fill of a valid drop target while dragging (primary). */
  dropTargetValidBg: channel("primary", "33 150 243", 0.25),
  /** Fill of an invalid drop target while dragging (danger). */
  dropTargetInvalidBg: channel("danger", "211 47 47", 0.2),
  /** Glow color used inside the drop-target box-shadow (success). */
  dropGlow: channel("success", "76 175 80", 0.4),

  /** Reference/link edit-cell base background (primary). */
  referenceCellBg: channel("primary", "25 118 210", 0.04),
  /** Reference/link edit-cell hover background (primary). */
  referenceCellHoverBg: channel("primary", "25 118 210", 0.08),

  /** New-row highlight base background (success). */
  newRowBg: channel("success", "76 175 80", 0.08),
  /** New-row highlight hover background (success). */
  newRowHoverBg: channel("success", "76 175 80", 0.12),
} as const;

/** Default swatch used when adding a hierarchy relationship-type color. */
export const HIERARCHY_DEFAULT_COLOR = "#446677";

export type CanvasColorToken = keyof typeof CANVAS_COLOR;

/** Full box-shadow for a canvas element that is an active drop target. */
export const CANVAS_DROP_TARGET_SHADOW = `0 0 8px ${CANVAS_COLOR.dropGlow}`;
